import { styled } from "@pigment-css/react";
import { Children, isValidElement, ReactNode, useMemo } from "react";
import { useLocale } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { currencyTokens, isNumericCell } from "@acto/taskpane/core/utils/numericCell";

// Layout follows the container's own width (not the viewport) via container queries,
// so a table reflows the same way in the narrow customer pane and in a wide one:
//   - 2 columns          → flat <dl> of key/value pairs
//   - 3+ columns, wide   → real <table>
//   - 3+ columns, narrow → stacked <dt>/<dd> cards; when col 0 is strictly 1..n
//                          (ranked), it becomes a "#N" badge and col 1 the card title
const STACK_BREAKPOINT = "640px";

const RANK_DIGIT_RE = /^\d+$/;

interface Cell {
  raw: string;
  node: ReactNode;
}

type Align = "left" | "right" | "center" | undefined;

interface ExtractedTable {
  headers: string[];
  rows: Cell[][];
  alignments: Align[];
}

function toText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(toText).join("");
  if (isValidElement(node)) return toText((node.props as { children?: ReactNode }).children);
  return "";
}

function extractTable(children: ReactNode): ExtractedTable {
  let headers: string[] = [];
  const rows: Cell[][] = [];
  const alignments: Align[] = [];

  Children.forEach(children as ReactNode, (section) => {
    if (!isValidElement(section)) return;
    const sectionType = section.type as { name?: string } | string;
    const isHead = sectionType === "thead" || (typeof sectionType === "object" && sectionType?.name === "thead");

    Children.forEach((section.props as { children?: ReactNode }).children, (tr) => {
      if (!isValidElement(tr)) return;
      const headerCells: string[] = [];
      const bodyCells: Cell[] = [];
      let colIdx = 0;
      Children.forEach((tr.props as { children?: ReactNode }).children, (cell) => {
        if (isValidElement(cell)) {
          // react-markdown converts GFM align attributes into style.textAlign,
          // so read both for forward-compatibility.
          const cellProps = cell.props as {
            align?: Align;
            style?: { textAlign?: Align };
          };
          const align = cellProps.align ?? cellProps.style?.textAlign;
          if (isHead && alignments[colIdx] === undefined) alignments[colIdx] = align;
        }
        const raw = toText(cell).trim();
        if (isHead) {
          headerCells.push(raw);
        } else {
          // Keep the cell's original children so inline markdown (links, bold) survives.
          const node = isValidElement(cell) ? (cell.props as { children?: ReactNode }).children : cell;
          bodyCells.push({ raw, node });
        }
        colIdx += 1;
      });
      if (isHead && headers.length === 0) headers = headerCells;
      else if (bodyCells.length) rows.push(bodyCells);
    });
  });

  return { headers, rows, alignments };
}

function firstColumnIsRank(rows: Cell[][]): boolean {
  if (rows.length < 2) return false;
  return rows.every((r, i) => {
    const v = r[0]?.raw.trim() ?? "";
    return RANK_DIGIT_RE.test(v) && Number.parseInt(v, 10) === i + 1;
  });
}

const QueryFrame = styled("div")({
  width: "100%",
  marginTop: "8px",
  marginBottom: "16px",
  containerType: "inline-size",
});

const FlatFrame = styled("div")({
  width: "100%",
  marginTop: "8px",
  marginBottom: "16px",
});

const WideWrapper = styled("div")(({ theme }) => ({
  display: "none",
  width: "100%",
  maxWidth: "100%",
  border: `1px solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
  borderRadius: "12px",
  background: theme.colors.neutral[0],
  [`@container (min-width: ${STACK_BREAKPOINT})`]: { display: "block" },
}));

const WideTable = styled("table")(({ theme }) => ({
  width: "100%",
  // auto (not fixed) so columns size to their content: "Rang" stays narrow while the
  // name/amount columns get the room they need, which is what stops the per-character
  // breaks once the equal-width fixed columns are gone.
  tableLayout: "auto",
  borderCollapse: "separate",
  borderSpacing: 0,
  fontFamily: "Inter, system-ui, sans-serif",
  fontSize: "14px",
  lineHeight: "140%",
  color: theme.fieldSalesTwo.colors.neutral[900],
  "& th, & td": {
    padding: "10px 12px",
    verticalAlign: "top",
    whiteSpace: "normal",
    // Prefer hyphenation at real syllable boundaries; fall back to break-word only for
    // tokens with no hyphenation point (URLs, IDs) so nothing ever overflows the bubble.
    WebkitHyphens: "auto",
    hyphens: "auto",
    overflowWrap: "break-word",
    textAlign: "left",
  },
  // Numbers/currency are kept on one line so amounts never split mid-digit.
  "& tbody td[data-numeric='true']": {
    whiteSpace: "nowrap",
    hyphens: "manual",
  },
  "& thead th": {
    background: theme.colors.neutral[50],
    fontWeight: 520,
    borderBottom: `1px solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
  },
  "& tbody td": {
    borderBottom: `1px solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
    background: theme.colors.neutral[0],
  },
  "& th[data-align='right'], & td[data-align='right']": { textAlign: "right" },
  "& th[data-align='center'], & td[data-align='center']": { textAlign: "center" },
  "& tbody tr:last-of-type td": { borderBottom: "none" },
}));

const StackWrapper = styled("div")({
  display: "block",
  width: "100%",
  [`@container (min-width: ${STACK_BREAKPOINT})`]: { display: "none" },
});

const StackBlock = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  padding: "14px 20px",
  borderBottom: `1px solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
  "&:last-of-type": { borderBottom: "none" },
}));

const TitleLine = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  gap: "8px",
  fontFamily: "Inter, system-ui, sans-serif",
  fontWeight: 520,
  fontSize: "15px",
  lineHeight: "148%",
  color: theme.fieldSalesTwo.colors.neutral[900],
  WebkitHyphens: "auto",
  hyphens: "auto",
  overflowWrap: "break-word",
}));

const RankBadge = styled("span")(({ theme }) => ({
  fontFamily: "Inter, system-ui, sans-serif",
  fontWeight: 500,
  fontSize: "13px",
  color: theme.fieldSalesTwo.colors.neutral[600],
  flexShrink: 0,
}));

const DetailList = styled("dl")({
  display: "grid",
  // Both tracks equally flexible (1fr/1fr), NOT 1fr/auto. An `auto` value track grows to
  // its max-content, so a long value (e.g. a 36-char order-ID UUID) eats the row and starves
  // the label track below its own min-content — at which point overflow-wrap: break-word
  // degrades to one-character-per-line and the label renders as a vertical strip. Equal 1fr
  // tracks cap each side at half the row, so a long value wraps within its half instead of
  // crushing the label. (Same one-char symptom as #701/#702 but a distinct cause: grid-track
  // starvation, not bubble min-content collapse or mid-word table breaks.)
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  rowGap: "6px",
  columnGap: "16px",
  margin: 0,
  padding: 0,
});

const DetailKey = styled("dt")(({ theme }) => ({
  fontFamily: "Inter, system-ui, sans-serif",
  fontWeight: 500,
  fontSize: "13px",
  lineHeight: "132%",
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflowWrap: "break-word",
}));

const DetailValue = styled("dd")(({ theme }) => ({
  fontFamily: "Inter, system-ui, sans-serif",
  fontWeight: 520,
  fontSize: "14px",
  lineHeight: "148%",
  color: theme.fieldSalesTwo.colors.neutral[900],
  textAlign: "right",
  margin: 0,
  overflowWrap: "break-word",
}));

const FlatDLWrapper = styled("div")({ width: "100%", padding: "0 20px" });

const FlatDL = styled("dl")(({ theme }) => ({
  display: "grid",
  // 1fr/1fr (not 1fr/auto) for the same reason as DetailList: an `auto` value track grows
  // to max-content and can starve the label into per-character wrapping. Here the risk is the
  // mirror case — a long label (e.g. "Durchschnittlicher eingehender Auftragswert") against a
  // short value — but the failure mode is identical, so both tracks stay equally flexible.
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  rowGap: "10px",
  columnGap: "16px",
  margin: 0,
  padding: "10px 0",
  borderTop: `1px solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
  borderBottom: `1px solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
  "& > dt": {
    fontFamily: "Inter, system-ui, sans-serif",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "148%",
    color: theme.fieldSalesTwo.colors.neutral[800],
    overflowWrap: "break-word",
  },
  "& > dd": {
    fontFamily: "Inter, system-ui, sans-serif",
    fontWeight: 520,
    fontSize: "15px",
    lineHeight: "148%",
    color: theme.fieldSalesTwo.colors.neutral[900],
    textAlign: "right",
    margin: 0,
    overflowWrap: "break-word",
  },
}));

const TwoColumnList = ({ rows }: { rows: Cell[][] }) => (
  <FlatFrame>
    <FlatDLWrapper>
      <FlatDL>
        {rows.flatMap((row, ri) => [
          <dt key={`k-${ri}`}>{row[0]?.node ?? row[0]?.raw ?? ""}</dt>,
          <dd key={`v-${ri}`}>{row[1]?.node ?? row[1]?.raw ?? ""}</dd>,
        ])}
      </FlatDL>
    </FlatDLWrapper>
  </FlatFrame>
);

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <>
    <DetailKey>{label}</DetailKey>
    <DetailValue>{value}</DetailValue>
  </>
);

export const ResponsiveTable = (props: React.HTMLAttributes<HTMLTableElement>) => {
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const tokens = useMemo(() => currencyTokens(locale, currency), [locale, currency]);

  const { headers, rows, alignments } = extractTable(props.children);
  if (rows.length === 0) return null;
  if (headers.length === 2) return <TwoColumnList rows={rows} />;

  const rankFirstCol = firstColumnIsRank(rows);
  // -1 marks "no promoted title"; ranked tables promote col 1 out of the detail list.
  const titleColIdx = rankFirstCol ? 1 : -1;

  return (
    // hyphens: auto only fires when an ancestor declares the content language, and the language
    // selects the syllable dictionary: German compounds ("Schnittwarenerfassung") break nowhere
    // near where English words do.
    <QueryFrame lang={locale}>
      <WideWrapper>
        <WideTable>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} data-align={alignments[i]}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    data-align={alignments[ci]}
                    data-numeric={isNumericCell(cell.raw, tokens) ? "true" : undefined}
                  >
                    {cell.node ?? cell.raw}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </WideTable>
      </WideWrapper>

      <StackWrapper role="list" aria-label={headers.join(", ")}>
        {rows.map((row, ri) => {
          const rank = rankFirstCol ? row[0]?.raw : "";
          const titleCell = row[titleColIdx];
          return (
            <StackBlock key={ri} role="listitem">
              {(titleCell || rank) && (
                <TitleLine>
                  {rank && <RankBadge>{`#${rank}`}</RankBadge>}
                  <span>{titleCell?.node ?? titleCell?.raw ?? ""}</span>
                </TitleLine>
              )}
              <DetailList>
                {row.map((cell, ci) => {
                  if (ci === titleColIdx) return null;
                  if (ci === 0 && rankFirstCol) return null;
                  return <DetailRow key={ci} label={headers[ci] ?? ""} value={cell.node ?? cell.raw} />;
                })}
              </DetailList>
            </StackBlock>
          );
        })}
      </StackWrapper>
    </QueryFrame>
  );
};
